import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { clearQueryCache } from '@/lib/queryClient'

export interface AdminUser {
  id: number
  email: string
  name: string
  role: string // legacy: "super_admin" | "admin" | "support"
  role_id?: number
  role_name?: string
  permissions?: string[] // RBAC permissions from JWT
  two_factor_enabled: boolean
}

interface AuthState {
  user: AdminUser | null
  accessToken: string | null
  refreshToken: string | null
  isAuthenticated: boolean
  setAuth: (user: AdminUser, accessToken: string, refreshToken: string) => void
  updateUser: (user: AdminUser) => void
  setTokens: (accessToken: string, refreshToken: string) => void
  logout: () => void
}

// Architecture decision: JWT tokens live in HttpOnly cookies set by the API.
// JavaScript cannot read them, which removes the XSS token-exfil vector.
// - Access/refresh cookies: HttpOnly + Secure + SameSite=Strict (server-side)
// - Request auth: `credentials: 'include'` on every fetch in `src/api/client.ts`
// - In-memory `accessToken` / `refreshToken` fields are RETAINED for phase-1
//   dual-write (backend still echoes tokens in the response body so the
//   Bearer-header fallback path works for CI / tooling). They are NO LONGER
//   persisted to localStorage, so a page refresh discards them — the cookie
//   (which outlives the tab) becomes the durable credential.
// - `user` is persisted as a UI hint only (no credential value by itself).
export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      setAuth: (user, accessToken, refreshToken) =>
        set({ user, accessToken, refreshToken, isAuthenticated: true }),
      updateUser: (user) => set({ user }),
      setTokens: (accessToken, refreshToken) =>
        set({ accessToken, refreshToken }),
      logout: () => {
        clearQueryCache()
        set({ user: null, accessToken: null, refreshToken: null, isAuthenticated: false })
      },
    }),
    {
      name: 'xident-admin-auth',
      // Persist only non-credential UI state. Tokens are intentionally
      // excluded so they never touch localStorage (XSS-exfil-safe).
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
)
